<template>
    <div class="blog-post-page">
        <!-- Article Header - only show for valid posts -->
        <Section
            v-if="isValidPost"
            class="article-header-section"
        >
            <div class="article-header-container">
                <router-link
                    :to="getLocalizedPath('/blog')"
                    class="back-link"
                >
                    <SmartIcon
                        icon="arrow-left"
                        :size="16"
                    />
                    {{ t('blog.back_to_blog') }}
                </router-link>
                <h1>{{ postData?.title }}</h1>
                <div class="article-meta">
                    <span class="article-date">{{ postData?.date }}</span>
                    <span class="article-separator">|</span>
                    <span class="article-reading-time"
                        >{{ postData?.reading_time }} {{ t('blog.reading_time') }}</span
                    >
                    <span class="article-separator">|</span>
                    <span class="article-author">{{ postData?.author }}</span>
                </div>
            </div>
        </Section>

        <!-- Article Content -->
        <Section class="article-content-section">
            <div class="article-container">
                <component
                    :is="articleComponent"
                    v-if="articleComponent"
                    :sections="sections"
                    :captions="captions"
                />

                <!-- Fallback for unknown posts -->
                <div
                    v-else
                    class="not-found"
                >
                    <router-link
                        :to="getLocalizedPath('/blog')"
                        class="back-link-not-found"
                    >
                        <SmartIcon
                            icon="arrow-left"
                            :size="16"
                        />
                        {{ t('blog.back_to_blog') }}
                    </router-link>
                    <h2>Blog post not found</h2>
                    <router-link :to="getLocalizedPath('/blog')">
                        {{ t('blog.back_to_blog') }}
                    </router-link>
                </div>
            </div>
        </Section>

        <DemoCredentialsDialog
            :model-value="showDemoDialog"
            :title="t('cta.demo_overlay.title')"
            :hint="t('cta.demo_overlay.hint')"
            :username-label="t('cta.demo_overlay.username')"
            :password-label="t('cta.demo_overlay.password')"
            :cancel-label="t('cta.demo_overlay.cancel')"
            :open-demo-label="t('cta.demo_overlay.open')"
            @update:model-value="showDemoDialog = $event"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onUnmounted, type Component } from 'vue'
    import { useRoute } from 'vue-router'
    import Section from '@/components/Section.vue'
    import SmartIcon from '@/components/common/SmartIcon.vue'
    import DemoCredentialsDialog from '@/components/DemoCredentialsDialog.vue'
    import { useSEO } from '@/composables/useSEO'
    import { useTranslations } from '@/composables/useTranslations'
    import type { PostSections, PostCaptions } from '@/composables/useBlogFormatter'
    import EntitiesAndDefinitionsArticle from '@/pages/blog/EntitiesAndDefinitionsArticle.vue'
    import EntityHierarchiesArticle from '@/pages/blog/EntityHierarchiesArticle.vue'
    import HeadlessAuthenticationArticle from '@/pages/blog/HeadlessAuthenticationArticle.vue'
    import EmailNotificationsArticle from '@/pages/blog/EmailNotificationsArticle.vue'

    interface PostData {
        title: string
        excerpt: string
        date: string
        reading_time: string
        author: string
        sections: PostSections
        captions?: PostCaptions
    }

    // Map of slugs to their article components. Adding a new post means:
    // 1) adding a line here, 2) a translation entry under blog.posts.<slug>,
    // 3) a sitemap slug in scripts/generate-sitemap.js, 4) an SSG route in vite.config.ts.
    const ARTICLE_COMPONENTS: Record<string, Component> = {
        'email-notifications': EmailNotificationsArticle,
        'entities-and-definitions': EntitiesAndDefinitionsArticle,
        'entity-hierarchies': EntityHierarchiesArticle,
        'headless-authentication': HeadlessAuthenticationArticle,
    }

    const { t, get, currentLanguage } = useTranslations()
    const route = useRoute()
    const showDemoDialog = ref(false)

    const slug = computed(() => route.params.slug as string)

    const articleComponent = computed(() => ARTICLE_COMPONENTS[slug.value])
    const isValidPost = computed(() => !!articleComponent.value)

    const getLocalizedPath = (path: string) => {
        const lang = (route.params.lang as string) || currentLanguage.value
        return `/${lang}${path === '/' ? '' : path}`
    }

    const postData = computed(() => {
        if (!isValidPost.value) {
            return null
        }
        return get<PostData>(`blog.posts.${slug.value}`)
    })

    const sections = computed(() => postData.value?.sections)
    const captions = computed(() => postData.value?.captions)

    // Listen for the open-demo event from Header
    const handleOpenDemo = () => {
        showDemoDialog.value = true
    }

    onMounted(() => {
        window.addEventListener('open-demo', handleOpenDemo)
    })

    onUnmounted(() => {
        window.removeEventListener('open-demo', handleOpenDemo)
    })

    useSEO({
        title: () => {
            if (!isValidPost.value) {
                return 'Not Found - RDataCore Blog'
            }
            const seoPost = get<{ title: string }>(`blog.seo_posts.${slug.value}`)
            return seoPost?.title ?? t('blog.seo.title')
        },
        description: () => {
            if (!isValidPost.value) {
                return 'The requested blog post was not found.'
            }
            const seoPost = get<{ description: string }>(`blog.seo_posts.${slug.value}`)
            return seoPost?.description ?? t('blog.seo.description')
        },
    })
</script>

<style scoped>
    .blog-post-page {
        padding-top: 80px;
    }

    /* Article Header */
    .article-header-section {
        padding: 60px 0 40px;
        background: linear-gradient(
            180deg,
            rgba(var(--v-theme-surface), 1) 0%,
            rgba(var(--v-theme-surface-variant), 0.3) 100%
        );
    }

    .article-header-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 0 16px;
    }

    .back-link,
    .back-link-not-found {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: rgb(var(--v-theme-primary));
        text-decoration: none;
        font-weight: 600;
        margin-bottom: 24px;
        transition: opacity 0.2s;
    }

    .back-link:hover,
    .back-link-not-found:hover {
        opacity: 0.7;
    }

    .article-header-section h1 {
        font-size: clamp(2rem, 4vw, 3rem);
        font-weight: 800;
        line-height: 1.2;
        margin: 0 0 20px;
        color: rgb(var(--v-theme-on-surface));
    }

    .article-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 0.95rem;
        color: rgb(var(--v-theme-on-surface-variant));
    }

    .article-separator {
        opacity: 0.5;
    }

    /* Article Content */
    .article-content-section {
        padding: 60px 0 80px;
    }

    .article-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 0 16px;
    }

    .article-container :deep(.article-content) {
        font-size: 1.125rem;
        line-height: 1.8;
        color: rgb(var(--v-theme-on-surface));
    }

    .article-container :deep(.content-section) {
        margin-bottom: 48px;
    }

    .article-container :deep(.content-section h2) {
        font-size: 1.75rem;
        font-weight: 700;
        margin: 0 0 24px;
        color: rgb(var(--v-theme-on-surface));
    }

    .article-container :deep(.content-paragraph) {
        margin: 0 0 20px;
        color: rgb(var(--v-theme-on-surface-variant));
    }

    .article-container :deep(.content-paragraph strong) {
        font-weight: 700;
        color: rgb(var(--v-theme-on-surface));
    }

    .article-container :deep(.content-paragraph code) {
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
        background: rgba(var(--v-theme-on-surface), 0.08);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.9em;
        color: rgb(var(--v-theme-primary));
    }

    .article-container :deep(.content-paragraph ul),
    .article-container :deep(.content-paragraph ol) {
        margin: 16px 0;
        padding-left: 24px;
    }

    .article-container :deep(.content-paragraph li) {
        margin-bottom: 8px;
        line-height: 1.6;
    }

    .article-container :deep(.content-paragraph li strong) {
        color: rgb(var(--v-theme-on-surface));
    }

    .article-container :deep(.content-paragraph a) {
        color: rgb(var(--v-theme-primary));
        text-decoration: none;
        font-weight: 600;
    }

    .article-container :deep(.content-paragraph a:hover) {
        text-decoration: underline;
    }

    .article-container :deep(.article-figure) {
        margin: 32px 0;
        text-align: center;
    }

    .article-container :deep(.article-figure img) {
        max-width: 100%;
        height: auto;
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
        border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
    }

    .article-container :deep(.article-figure--narrow img) {
        max-width: 70%;
    }

    .article-container :deep(.article-figure figcaption) {
        margin-top: 12px;
        font-size: 0.9rem;
        color: rgb(var(--v-theme-on-surface-variant));
        font-style: italic;
    }

    .article-container :deep(.teaser-section) {
        background: rgba(var(--v-theme-primary), 0.05);
        padding: 32px;
        border-radius: 16px;
        border: 1px solid rgba(var(--v-theme-primary), 0.2);
    }

    .article-container :deep(.coming-soon-badge) {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgb(var(--v-theme-primary));
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        font-weight: 600;
        margin-top: 16px;
    }

    .not-found {
        text-align: center;
        padding: 80px 0;
    }

    .not-found h2 {
        margin-bottom: 24px;
        color: rgb(var(--v-theme-on-surface));
    }

    .not-found a:not(.back-link-not-found) {
        color: rgb(var(--v-theme-primary));
        text-decoration: none;
        font-weight: 600;
    }

    @media (max-width: 960px) {
        .blog-post-page {
            padding-top: 60px;
        }

        .article-header-section {
            padding: 40px 0 30px;
        }

        .article-content-section {
            padding: 40px 0 60px;
        }

        .article-container :deep(.article-content) {
            font-size: 1rem;
        }

        .article-container :deep(.content-section h2) {
            font-size: 1.5rem;
        }

        .article-meta {
            flex-direction: column;
            gap: 4px;
        }

        .article-separator {
            display: none;
        }

        .article-container :deep(.teaser-section) {
            padding: 24px;
        }
    }
</style>
